/**
 * useAuthContext.tsx (AGGIORNATO)
 * 
 * Context di autenticazione con gestione sessione migliorata.
 * 
 * MODIFICHE:
 * 1. checkSession chiamato anche al focus della finestra
 * 2. Polling periodico della sessione
 * 3. Gestione corretta del redirect
 * 4. Integrazione con SessionManager
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { ChildrenType } from '../types/component-props';
import { UsersType } from '@/types/auth';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { checkSession } from '../redux/features/core/authentication/authenticationSlice';
import { logout, resetAuthentication } from '../redux/features/core/authentication/authenticationActions';
import { routesIndex } from '@/RoutesIndex';
import { sliceStateStatus } from '@/Core';

// =============================================================================
// TYPES
// =============================================================================

export type AuthContextType = {
  user: UsersType | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  lastChecked: number | null;
  saveSession: (session: UsersType) => void;
  removeSession: () => void;
  refreshSession: () => Promise<boolean>;
};

// =============================================================================
// CONTEXT
// =============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

// =============================================================================
// CONFIGURATION
// =============================================================================

const CONFIG = {
  // Intervallo minimo tra check sessione (evita flood)
  MIN_CHECK_INTERVAL: 30 * 1000, // 30 secondi
  
  // Check sessione quando la finestra torna in focus
  CHECK_ON_FOCUS: true,
  
  // Check sessione quando il tab torna visibile
  CHECK_ON_VISIBILITY: true,
  
  // Routes pubbliche (no redirect se non autenticato)
  PUBLIC_ROUTES: [
    '/autenticazione',
    '/registrazione',
    '/passworddimenticata',
    '/confermapassword',
    '/resetpassword',
    '/auth/',
  ]
};

// =============================================================================
// PROVIDER
// =============================================================================

export function AuthProvider({ children }: ChildrenType) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Redux state
  const xAuthentication = useAppSelector((state) => state.authentication);
  const isAuthenticated = xAuthentication.data?.isAuthenticated ?? false;
  const user = xAuthentication.data?.user;
  const authStatus = xAuthentication.status;

  // Local state
  const [isLoading, setIsLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState<number | null>(null);
  
  // Refs
  const isCheckingRef = useRef(false);
  const mountedRef = useRef(true);

  // ===========================================================================
  // HELPERS
  // ===========================================================================

  /**
   * Verifica se il path corrente è pubblico
   */
  const isPublicRoute = useCallback((path: string): boolean => {
    return CONFIG.PUBLIC_ROUTES.some(route => path.startsWith(route));
  }, []);

  /**
   * Può eseguire un check sessione? (throttling)
   */
  const canCheck = useCallback((): boolean => {
    if (!lastChecked) return true;
    return Date.now() - lastChecked > CONFIG.MIN_CHECK_INTERVAL;
  }, [lastChecked]);

  // ===========================================================================
  // SESSION MANAGEMENT
  // ===========================================================================

  /**
   * Verifica la sessione con il server
   */
  const refreshSession = useCallback(async (): Promise<boolean> => {
    if (isCheckingRef.current) {
      console.log('[AuthContext] Check already in progress, skipping');
      return isAuthenticated;
    }

    if (!canCheck()) {
      console.log('[AuthContext] Check throttled, skipping');
      return isAuthenticated;
    }

    isCheckingRef.current = true;
    console.log('[AuthContext] Checking session...');

    try {
      const result = await dispatch(checkSession());
      
      if (mountedRef.current) {
        setLastChecked(Date.now());
        setIsLoading(false);
      }

      // checkSession è un thunk che dispatcha me() e gestisce errori
      // Se me() fallisce, resetAuthentication viene chiamato
      return true;
    } catch (error) {
      console.error('[AuthContext] Session check failed:', error);
      
      if (mountedRef.current) {
        setIsLoading(false);
      }
      return false;
    } finally {
      isCheckingRef.current = false;
    }
  }, [dispatch, canCheck, isAuthenticated]);

  /**
   * Logout manuale
   */
  const removeSession = useCallback(() => {
    console.log('[AuthContext] Removing session');
    dispatch(logout());
    dispatch(resetAuthentication());
    navigate(routesIndex.app.authentication, { replace: true });
  }, [dispatch, navigate]);

  /**
   * Placeholder per compatibilità
   */
  const saveSession = useCallback((session: UsersType) => {
    // Non usato con Redux, ma mantenuto per compatibilità
    console.warn('[AuthContext] saveSession called but not implemented with Redux');
  }, []);

  // ===========================================================================
  // EFFECTS
  // ===========================================================================

  /**
   * Check iniziale al mount
   */
  useEffect(() => {
    mountedRef.current = true;
    
    // Se siamo su una route pubblica, non serve check
    if (isPublicRoute(location.pathname)) {
      setIsLoading(false);
      return;
    }

    // Check sessione iniziale
    refreshSession();

    return () => {
      mountedRef.current = false;
    };
  }, []); // Solo al mount

  /**
   * Check quando la finestra torna in focus
   */
  useEffect(() => {
    if (!CONFIG.CHECK_ON_FOCUS) return;

    const handleFocus = () => {
      if (isAuthenticated && !isPublicRoute(location.pathname)) {
        console.log('[AuthContext] Window focused, checking session');
        refreshSession();
      }
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, location.pathname, refreshSession, isPublicRoute]);

  /**
   * Check quando il tab torna visibile
   */
  useEffect(() => {
    if (!CONFIG.CHECK_ON_VISIBILITY) return;

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' && 
        isAuthenticated && 
        !isPublicRoute(location.pathname)
      ) {
        console.log('[AuthContext] Tab became visible, checking session');
        refreshSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, location.pathname, refreshSession, isPublicRoute]);

  /**
   * Redirect a login se non autenticato su route protetta
   */
  useEffect(() => {
    // Aspetta che il loading iniziale sia completato
    if (isLoading) return;
    
    // Non fare nulla su route pubbliche
    if (isPublicRoute(location.pathname)) return;

    // Se non autenticato, redirect a login
    if (!isAuthenticated) {
      console.log('[AuthContext] Not authenticated, redirecting to login');
      navigate(routesIndex.app.authentication, {
        replace: true,
        state: { 
          from: location.pathname,
          sessionExpired: authStatus === sliceStateStatus.rejected
        }
      });
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate, isPublicRoute, authStatus]);

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        lastChecked,
        saveSession,
        removeSession,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
